import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Space,
  Collapse,
  Checkbox,
  Modal,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import PermissionService, { Permission, PermissionCategory } from '../services/permission';
import RoleService from '../services/role';
import { getColumnDisplayName, getPageActionDisplayName, getFormFieldDisplayName } from '../config/columns';
import {
  getServiceColumnsUnion,
  start_columns,
  end_columns,
  isOrderBaseColumn,
  getOrderFieldServiceKeys,
} from '../config/order-columns-config';
import { ORDER_SERVICE_ACCESS_LABELS } from '../config/order-service-access';
import { getApiResponseErrorMessage } from '../utils/error';

const { Text } = Typography;

// 角色权限编辑页面：将原来 Tab 中的编辑区域改为独立页面
const RolePermissionEdit: React.FC = () => {
  useDocumentTitle('角色权限 - 用户');
  const navigate = useNavigate();
  const params = useParams();
  const roleIdParam = params.id as string;
  const roleId = useMemo(() => parseInt(roleIdParam || '0', 10), [roleIdParam]);

  const [roleName, setRoleName] = useState<string>('');
  const [roleKey, setRoleKey] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [permissionCategories, setPermissionCategories] = useState<PermissionCategory[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [tempPermissionNames, setTempPermissionNames] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  // 加载角色信息
  const loadRole = async (id: number) => {
    try {
      const resp = await RoleService.getRole(id);
      if (resp.success && resp.data) {
        setRoleName(resp.data.display_name || resp.data.name);
        setRoleKey(resp.data.name);
      }
    } catch (e) {
      // ignore
    }
  };

  // 加载权限分组数据
  const loadPermissionCategories = async () => {
    try {
      const categoriesResponse = await PermissionService.getPermissionsByCategory();
      if (categoriesResponse.success && categoriesResponse.data) {
        setPermissionCategories(categoriesResponse.data);
      }
    } catch (error) {
      message.error('加载权限数据失败');
    }
  };

  // 加载角色权限
  const loadRolePermissions = async (id: number) => {
    try {
      const response = await PermissionService.getRolePermissions(id);
      if (response.success && response.data) {
        setRolePermissions(response.data);
        setTempPermissionNames(response.data.map(p => p.name));
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      message.error('加载角色权限失败');
    }
  };

  // 更新临时权限（不立即保存）
  const updateTempPermissions = (permissionNames: string[]) => {
    setTempPermissionNames(permissionNames);
    const original = rolePermissions.map(p => p.name).sort();
    const current = permissionNames.slice().sort();
    setHasUnsavedChanges(JSON.stringify(original) !== JSON.stringify(current));
  };

  // 保存权限
  const saveRolePermissions = async () => {
    if (!roleId) return;
    setSaving(true);
    try {
      const resp = await PermissionService.assignRolePermissions(roleId, tempPermissionNames);
      if (resp.success) {
        message.success('权限保存成功');
        await loadRolePermissions(roleId);
      } else {
        message.error(getApiResponseErrorMessage(resp, '保存失败'));
      }
    } finally {
      setSaving(false);
    }
  };

  // 重置权限更改
  const resetPermissions = () => {
    setTempPermissionNames(rolePermissions.map(p => p.name));
    setHasUnsavedChanges(false);
  };

  // 搜索过滤
  const getFilteredPermissionCategories = (): PermissionCategory[] => {
    if (!searchText.trim()) return permissionCategories;
    const s = searchText.toLowerCase();
    return permissionCategories
      .map(cat => ({
        ...cat,
        permissions: cat.permissions.filter(p =>
          p.name.toLowerCase().includes(s) ||
          p.display_name?.toLowerCase().includes(s) ||
          p.description?.toLowerCase().includes(s) ||
          getPermissionDisplayName(p).toLowerCase().includes(s)
        )
      }))
      .filter(cat => cat.permissions.length > 0);
  };

  // 权限按类型分组
  const groupPermissionsByType = (permissions: Permission[]) => {
    const groups: Record<string, Permission[]> = {};
    permissions.forEach(p => {
      let type = 'other';
      if (
        p.name.includes('.view') &&
        (
          p.name.match(/^page\.[^.]+\.view$/) ||
          p.name.match(/^page\.order-management\.service\.[^.]+\.view$/)
        )
      ) type = 'view';
      else if (p.name.includes('.action.')) type = 'action';
      else if (p.name.includes('.column.')) type = 'column';
      else if (p.name.includes('.form.')) type = 'form';
      (groups[type] ||= []).push(p);
    });
    const order = ['view', 'action', 'column', 'form', 'other'];
    const names: Record<string, string> = {
      view: '页面访问权限',
      action: '操作权限',
      column: '列显示权限',
      form: '表单字段权限',
      other: '其他权限',
    };
    return order
      .filter(t => groups[t]?.length)
      .map(t => ({ type: t, typeName: names[t], permissions: groups[t] }));
  };

  // 业务订单：将列/表单权限按 service 分组
  const groupOrderPermissionsByService = (permissions: Permission[], as: 'column' | 'form') => {
    const groups: Record<string, { label: string; items: Permission[] }> = {
      base: { label: '基本信息', items: [] },
      trademark: { label: '商标服务', items: [] },
      patent: { label: '专利服务', items: [] },
      copyright: { label: '版权服务', items: [] },
      company: { label: '工商服务', items: [] },
      barcode: { label: '条码服务', items: [] },
      other: { label: '其他服务', items: [] },
    };

    const baseFields = new Set<string>([...start_columns, ...end_columns]);

    // 按服务收集所有列 key，避免手工维护遗漏
    const buckets: Record<string, string[]> = {
      trademark: getServiceColumnsUnion('trademark'),
      patent: getServiceColumnsUnion('patent'),
      copyright: getServiceColumnsUnion('copyright'),
      company: getServiceColumnsUnion('company'),
      barcode: getServiceColumnsUnion('barcode'),
      other: getServiceColumnsUnion('other'),
    };

    // 如果存在按服务细分的 sub_service 权限，则隐藏通用权限
    const perms = (() => {
      const names = permissions.map(p => p.name);
      if (as === 'column') {
        const hasSvc = names.some(n => /^page\.order-management\.column\.[^.]+\.sub_service$/.test(n));
        return hasSvc ? permissions.filter(p => p.name !== 'page.order-management.column.sub_service') : permissions;
      } else {
        const hasSvc = names.some(n => /^page\.order-management\.form\.[^.]+\.sub_service\.(view|edit)$/.test(n));
        return hasSvc ? permissions.filter(p => !/^page\.order-management\.form\.sub_service\.(view|edit)$/.test(p.name)) : permissions;
      }
    })();

    perms.forEach(p => {
      const parts = p.name.split('.');
      const pageKey = parts[1];
      if (pageKey !== 'order-management') {
        groups.other.items.push(p);
        return;
      }
      const field = as === 'column'
        ? (parts.length === 5 ? parts[4] : parts[parts.length - 1])
        : (parts.length === 6 ? parts[4] : parts[parts.length - 2]);
      const serviceHint = (() => {
        if (as === 'column') return parts.length === 5 ? parts[3] : undefined;
        if (as === 'form') return parts.length === 6 ? parts[3] : undefined;
        return undefined;
      })();
      // sub_service 视为全局字段：仅在基本信息中展示一次，忽略按服务拆分的重复项
      if (field === 'sub_service') {
        if (groups.base.items.some(item => item.name.includes('.column.sub_service') || item.name.includes('.form.sub_service'))) {
          return;
        }
        groups.base.items.push(p);
        return;
      }

      if (isOrderBaseColumn(field) || baseFields.has(field)) {
        groups.base.items.push(p);
        return;
      }
      if (serviceHint && (serviceHint in groups)) {
        (groups[serviceHint as keyof typeof groups].items).push(p);
        return;
      }

      // 一个字段可能属于多个服务（如申请号在商标/版权均存在），全部归入以便权限面板完整展示
      const matchedBuckets = getOrderFieldServiceKeys(field).length > 0
        ? getOrderFieldServiceKeys(field)
        : (Object.keys(buckets) as Array<keyof typeof buckets>).filter(svc => buckets[svc].includes(field));

      if (matchedBuckets.length > 0) {
        matchedBuckets.forEach(svc => {
          if (svc in groups) {
            groups[svc].items.push(p);
          }
        });
        return;
      }

      groups.other.items.push(p);
    });

    const order: (keyof typeof groups)[] = ['base','trademark','patent','copyright','company','barcode','other'];
    return order.filter(k => groups[k].items.length > 0).map(k => ({ key: k, label: groups[k].label, permissions: groups[k].items }));
  };

  // 权限友好显示名称
  const getPermissionDisplayName = (permission: Permission): string => {
    if (permission.display_name) return permission.display_name;
    const name = permission.name;
    const serviceViewMatch = name.match(/^page\.order-management\.service\.([^.]+)\.view$/);
    if (serviceViewMatch) {
      const serviceKey = serviceViewMatch[1];
      return `访问业务订单 - ${ORDER_SERVICE_ACCESS_LABELS[serviceKey] || serviceKey}`;
    }
    if (/^page\.[^.]+\.view$/.test(name)) {
      const page = name.split('.')[1];
      const map: Record<string, string> = {
        'order-management': '业务订单',
        'fee-management': '最新报价',
        'follow-up-management': '客户跟进',
        'trademark-monitor': '商标监控',
        'status-translation': '状态翻译',
        'document-management': '文档管理',
      };
      return `访问${map[page] || page}页面`;
    }
    if (name.includes('.action.')) {
      const parts = name.split('.');
      const pageKey = parts[1];
      return getPageActionDisplayName(pageKey, parts[parts.length - 1]);
    }
    if (name.includes('.column.')) {
      const parts = name.split('.');
      const pageKey = parts[1];
      const column = parts[parts.length - 1];
      const label = getColumnDisplayName(pageKey, column);
      return `${column}: ${label}`;
    }
    if (name.includes('.form.')) {
      const parts = name.split('.');
      const pageKey = parts[1];
      const field = parts[parts.length - 2];
      return `${field}: ${getFormFieldDisplayName(pageKey, field)}`;
    }
    return permission.name;
  };

  // 渲染权限内容
  const renderContent = () => {
    if (!roleId) return null;
    // 管理员提示
    if (roleKey === 'admin') {
      return (
        <div style={{ textAlign: 'center', padding: '60px 40px', background: '#f8f9fa', borderRadius: 8, margin: '20px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔑</div>
          <h3 style={{ color: '#1890ff', marginBottom: 12 }}>管理员</h3>
          <p style={{ color: '#666', fontSize: 16, marginBottom: 16 }}>管理员自动拥有系统所有权限，无需手动配置</p>
          <p style={{ color: '#999', fontSize: 14 }}>这包括所有页面访问、操作执行、数据查看和系统管理权限</p>
        </div>
      );
    }

    return (
      <>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={14}>
            <Input
              placeholder="搜索权限..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
        </Row>

        <Collapse
          items={getFilteredPermissionCategories().map(category => ({
            key: category.category,
            label: `${category.category} (${category.permissions.length}个权限)`,
            children: (
              <>
                {/* 说明块 */}
                {(() => {
                  const key = category.category;
                  const hints: Record<string, string> = {
                    '业务订单': '按“页面访问/操作/列/表单”分类，列与表单进一步按服务细分',
                    '商标监控': '控制监控列表列、操作、表单字段显示权限',
                    '状态翻译': '控制页面访问与数据操作权限',
                    '最新报价': '控制报价列表列、表单字段与操作权限',
                    '客户跟进': '控制跟进页面访问、列、表单与操作',
                    '文档管理': '控制文档页面访问、列与操作权限',
                    '用户权限': '与角色/用户管理相关权限',
                    '系统管理': '系统级别设置与日志等权限',
                  };
                  const t = hints[key];
                  return t ? (
                    <div style={{ background: '#fafafa', border: '1px dashed #e8e8e8', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                      <Text type="secondary">{t}</Text>
                    </div>
                  ) : null;
                })()}

                {/* 复选区域 */}
                <Checkbox.Group
                  value={tempPermissionNames}
                  // 重要修复：在某一分类内勾选时，保留其他分类的选择状态
                  // Checkbox.Group 的 onChange 仅返回当前分组内被选中的值，
                  // 直接覆盖会丢失其他分组的勾选。这里将“当前分组所含权限”替换为最新选择，
                  // 同时保留不属于该分组的既有选择。
                  onChange={(vals) => {
                    const currentGroupNames = new Set(
                      category.permissions.map(p => p.name)
                    );
                    const preserved = tempPermissionNames.filter(
                      name => !currentGroupNames.has(name)
                    );
                    updateTempPermissions([
                      ...preserved,
                      ...((vals as string[]))
                    ]);
                  }}
                  style={{ width: '100%', display: 'block' }}
                >
                  {groupPermissionsByType(category.permissions).map(typeGroup => (
                    <div key={typeGroup.type} style={{ marginBottom: 16, width: '100%' }}>
                      <div style={{ marginBottom: 8 }}>
                        <Text strong>
                          {typeGroup.typeName}
                          <Text type="secondary" style={{ marginLeft: 8 }}>（{typeGroup.permissions.length}）</Text>
                        </Text>
                        <Space size="small" style={{ marginLeft: 8 }}>
                          <Button size="small" onClick={() => {
                            const current = tempPermissionNames;
                            const perms = typeGroup.permissions.map(p => p.name);
                            updateTempPermissions(Array.from(new Set(current.concat(perms))));
                          }}>本组全选</Button>
                          <Button size="small" onClick={() => {
                            const current = tempPermissionNames;
                            const perms = typeGroup.permissions.map(p => p.name);
                            updateTempPermissions(current.filter(p => !perms.includes(p)));
                          }}>本组取消</Button>
                        </Space>
                      </div>

                      {/* 订单页面的列/表单按服务细分 */}
                      {((typeGroup.type === 'column') || (typeGroup.type === 'form')) && category.category === '业务订单' ? (
                        <>
                          {groupOrderPermissionsByService(typeGroup.permissions, typeGroup.type as 'column' | 'form').map(svcGroup => (
                            <div key={svcGroup.key} style={{ marginBottom: 12, width: '100%' }}>
                              <div style={{ marginBottom: 6 }}>
                                <Text>{svcGroup.label}</Text>
                                <Space size="small" style={{ marginLeft: 8 }}>
                                  <Button size="small" onClick={() => {
                                    const current = tempPermissionNames;
                                    const perms = svcGroup.permissions.map(p => p.name);
                                    updateTempPermissions(Array.from(new Set(current.concat(perms))));
                                  }}>本组全选</Button>
                                  <Button size="small" onClick={() => {
                                    const current = tempPermissionNames;
                                    const perms = svcGroup.permissions.map(p => p.name);
                                    updateTempPermissions(current.filter(p => !perms.includes(p)));
                                  }}>本组取消</Button>
                                </Space>
                              </div>
                              <Row gutter={[16, 8]}>
                                {svcGroup.permissions.map(permission => (
                                  <Col span={6} key={permission.id}>
                                    <Checkbox value={permission.name}>
                                      <span title={permission.description || permission.name}>{getPermissionDisplayName(permission)}</span>
                                    </Checkbox>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          ))}
                        </>
                      ) : (
                        <Row gutter={[16, 8]}>
                          {typeGroup.permissions.map(permission => (
                            <Col
                              key={permission.id}
                              // 页面访问权限 与 操作权限：每项独占一行，避免布局错乱
                              span={(typeGroup.type === 'view' || typeGroup.type === 'action') ? 6 : 6}
                            >
                              <Checkbox value={permission.name}>
                                <span title={permission.description || permission.name}>{getPermissionDisplayName(permission)}</span>
                              </Checkbox>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  ))}
                </Checkbox.Group>
              </>
            )
          }))}
        />
      </>
    );
  };

  // 初始化加载
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadRole(roleId), loadPermissionCategories(), loadRolePermissions(roleId)]);
      } finally {
        setLoading(false);
      }
    })();
  }, [roleId]);

  // 离开页面提示未保存
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const handleBack = () => {
    if (!hasUnsavedChanges) {
      navigate('/roles');
      return;
    }
    Modal.confirm({
      title: '有未保存的更改',
      content: '当前角色的权限设置尚未保存，是否要放弃这些更改？',
      okText: '放弃更改',
      cancelText: '取消',
      onOk: () => navigate('/roles')
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
              返回
            </Button>
            <span>编辑角色权限</span>
            {roleName && (
              <Text type="secondary">（{roleName}）</Text>
            )}
          </Space>
        }
        loading={loading}
        extra={
          <Space>
            <Button onClick={resetPermissions} disabled={!hasUnsavedChanges}>重置</Button>
            <Button type="primary" onClick={saveRolePermissions} loading={saving} disabled={!hasUnsavedChanges}>保存</Button>
          </Space>
        }
      >
        {renderContent()}
      </Card>
    </div>
  );
};

export default RolePermissionEdit;
